import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabaseClient';

function PendingUsersPage() {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  useEffect(() => {
    // Redirect if not an admin or super admin
    if (!loading && userRole !== 'admin' && userRole !== 'super_admin') {
      navigate('/');
      return;
    }
    
    if (userRole === 'admin' || userRole === 'super_admin') {
      fetchPendingUsers();
    }
  }, [userRole, loading, navigate]);

  const fetchPendingUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('pending_users')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPendingUsers(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      // If user is a super admin, they can directly approve users as admins
      if (userRole === 'super_admin') {
        const { error } = await supabase.rpc('approve_user_as_admin', {
          pending_user_id: userId,
          super_admin_id: user.id
        });
        if (error) throw error;
      } else {
        // Regular admin approval flow
        const { error } = await supabase.rpc('approve_user', {
          pending_user_id: userId,
          admin_id: user.id
        });
        if (error) throw error;
      }
      await fetchPendingUsers();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleReject = async () => {
    if (!selectedUser) return;

    try {
      const { error } = await supabase
        .from('pending_users')
        .update({
          status: 'rejected',
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          rejection_reason: rejectionReason
        })
        .eq('id', selectedUser.id);

      if (error) throw error;
      setRejectDialogOpen(false);
      setRejectionReason('');
      setSelectedUser(null);
      await fetchPendingUsers();
    } catch (error) {
      setError(error.message);
    }
  };

  const openRejectDialog = (user) => {
    setSelectedUser(user);
    setRejectDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 1, sm: 2 },
        mb: { xs: 2, sm: 3 },
      }}>
        <Typography 
          variant="h4" 
          gutterBottom={false}
          sx={{ 
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' },
            mb: 0,
          }}
        >
        Pending Users
        </Typography>
        {userRole === 'super_admin' && (
          <Chip 
            label="Super Admin" 
            color="secondary" 
            size="small" 
            sx={{ 
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
            }}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          {error}
        </Alert>
      )}

      {userRole === 'super_admin' && (
        <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          As a super admin, you can approve users directly as administrators.
        </Alert>
      )}

      <Paper sx={{ width: '100%', mb: { xs: 2, sm: 2 } }}>
        {pendingUsers.length === 0 ? (
          <Box sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              No Pending Users
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mt: 1,
                fontSize: { xs: '0.875rem', sm: '1rem' },
              }}
            >
              There are currently no users waiting for approval.
            </Typography>
          </Box>
        ) : (
          <TableContainer
            sx={{
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
              },
            }}
          >
            <Table sx={{ minWidth: { xs: 600, sm: 'auto' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    whiteSpace: 'nowrap',
                  }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    whiteSpace: 'nowrap',
                    display: { xs: 'none', md: 'table-cell' },
                  }}>
                    Email
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    whiteSpace: 'nowrap',
                  }}>
                    User Type
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    whiteSpace: 'nowrap',
                    display: { xs: 'none', sm: 'table-cell' },
                  }}>
                    Registration Date
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    whiteSpace: 'nowrap',
                  }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingUsers.map((pendingUser) => (
                  <TableRow key={pendingUser.id}>
                    <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {pendingUser.first_name} {pendingUser.last_name}
                        </Typography>
                        {/* Show email on mobile */}
                        {isMobile && (
                          <Typography 
                            variant="caption" 
                            color="textSecondary"
                            sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                          >
                            {pendingUser.email}
                          </Typography>
                        )}
                        {/* Show registration date on mobile */}
                        {isMobile && (
                          <Typography 
                            variant="caption" 
                            color="textSecondary"
                            sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                          >
                            {new Date(pendingUser.created_at).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      display: { xs: 'none', md: 'table-cell' },
                    }}>
                      {pendingUser.email}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      <Chip
                        label={pendingUser.user_type}
                        color={
                          pendingUser.user_type === 'teacher'
                            ? 'primary'
                            : pendingUser.user_type === 'student'
                            ? 'secondary'
                            : 'default'
                        }
                        sx={{ 
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          height: { xs: '20px', sm: '24px' },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      display: { xs: 'none', sm: 'table-cell' },
                    }}>
                      {new Date(pendingUser.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 0.5, sm: 1 },
                      }}>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => handleApprove(pendingUser.id)}
                          fullWidth={isMobile}
                          sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            minWidth: { xs: '100%', sm: 'auto' },
                          }}
                      >
                        {userRole === 'super_admin' ? 'Approve as Admin' : 'Approve'}
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => openRejectDialog(pendingUser)}
                          fullWidth={isMobile}
                          sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            minWidth: { xs: '100%', sm: 'auto' },
                          }}
                      >
                        Reject
                      </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog 
        open={rejectDialogOpen} 
        onClose={() => setRejectDialogOpen(false)}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            maxHeight: { xs: '100vh', sm: '90vh' },
            margin: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Reject Registration
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason"
            fullWidth
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 1 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}>
          <Button 
            onClick={() => setRejectDialogOpen(false)}
            fullWidth={isMobile}
            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleReject} 
            color="error"
            variant="contained"
            fullWidth={isMobile}
            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PendingUsersPage; 